import type React from 'react'

import {Features} from 'airship/clients/feature-loader/types'
import NavBar from 'airship/components/AppNav'
import type {Menu} from 'airship/components/AppNav/types'
import FeatureLoader from 'airship/components/FeatureLoader'
import APP_KEY from 'airship/modules/app_key'
import getUrl from 'airship/modules/get-url'
import {track} from 'airship/modules/track'
import * as waffle from 'airship/modules/waffle'
import WaffleFlag from 'airship/modules/waffle/flags'

export const MENU_FEATURES = [
  Features.MessageNav,
  Features.MessageOverview,
  Features.AppleNewsComposer,
  Features.ContentAuthor,
  Features.ContentTemplates,
  Features.PushTemplates,
  Features.JourneysRead,
  Features.ActivityLog,
  Features.PushComposer,
  Features.Configuration,
  Features.MessageCenter,
  Features.Reports,
  Features.Insight,
  Features.SegmentsRead,
  Features.DeviceLookup,
  Features.StaticLists,
  Features.Tags,
  Features.ReportsOptin,
  Features.ReportsResponse,
  Features.ReportsAudience,
  Features.ReportsEmail,
  Features.CustomEventsRead,
  Features.SmsWorkflows,
  Features.ReportsSms,
  Features.ReportsWeb,
  Features.Survey,
  Features.SettingsPage,
  Features.Scene,
  Features.FeatureFlags,
  Features.FeatureFlagsHomeAccess,
  Features.HoldoutExperiments,
  Features.ExperimentsOverview,
  Features.WebPages,
  Features.ImageHostingAccess,
  Features.RfmAnalysis,
  Features.HasApptimize,
] as const

const GlobalNavBar: React.FC = () => {
  return (
    <FeatureLoader
      resolve={MENU_FEATURES}
      render={(features) => {
        return <NavBar menu={buildMenu(features)} />
      }}
    ></FeatureLoader>
  )
}

export default GlobalNavBar

export function buildMenu(
  features: Record<(typeof MENU_FEATURES)[number], boolean>
): Menu {
  return [
    {
      label: 'Messages',
      // baking the feature check into the components might make this a little nicer,
      // e.g. you'd just pass the feature enum value(s) as part of the object and pass the
      // full feature object to the component and it'd check for you instead of this
      // repetitive and kind-of obscured method
      hidden: !features[Features.MessageNav],
      children: [
        {
          label: 'Messages Overview',
          url: getUrl('messages:apps_messages_index', APP_KEY),
          hidden: !features[Features.MessageOverview],
        },
        {
          label: 'SMS Keywords',
          url: getUrl('sms:workflows_overview', APP_KEY),
          hidden: !features[Features.SmsWorkflows],
        },
        {
          label: 'Legacy Templates',
          url: getUrl('messages:apps_push_templates', APP_KEY),
          hidden: !features[Features.PushTemplates],
        },
        {
          label: 'Apple News',
          url: getUrl('apple_news:apple_news_index', APP_KEY),
          hidden: !features[Features.AppleNewsComposer],
        },
        {
          label: 'Activity Log',
          url: getUrl('messages:apps_activity_log', APP_KEY),
          hidden: !features[Features.ActivityLog],
        },
        {
          label: 'Welcome Message',
          url: getUrl('airmail:apps_set_welcome_message', APP_KEY),
          hidden:
            !features[Features.PushComposer] ||
            !features[Features.MessageCenter],
        },
      ],
    },
    {
      label: 'Content',
      hidden: !features[Features.ContentTemplates],
      children: [
        {
          label: 'Templates',
          url: getUrl(
            'content_templates_nav:app_content_templates_index',
            APP_KEY
          ),
        },
        {
          label: 'Scene Layouts',
          url: getUrl(
            'content_templates_nav:app_content_templates_layouts',
            APP_KEY
          ),
          hidden: !features[Features.Scene],
        },
        {
          label: 'Snippets',
          url: getUrl(
            'content_templates_nav:app_content_templates_snippets',
            APP_KEY
          ),
        },
        {
          label: 'Web Pages',
          url: getUrl(
            'content_templates_nav:app_content_templates_web_pages',
            APP_KEY
          ),
          hidden: !features[Features.WebPages],
        },
        {
          label: 'Media',
          url: getUrl(
            'content_templates_nav:app_content_templates_media',
            APP_KEY
          ),
          hidden: !features[Features.ImageHostingAccess],
        },
      ],
    },
    {
      label: 'Journeys',
      url: getUrl('apps:apps_journeys_hq_map', APP_KEY),
      hidden: !features[Features.JourneysRead],
    },
    {
      label: 'Audience',
      children: [
        {
          label: 'Behavioral Targeting',
          isNew: true,
          url: getUrl('rfm_analysis:apps_rfm_analysis', APP_KEY),
          hidden: !features[Features.RfmAnalysis],
        },
        {
          label: 'Segments',
          url: getUrl('advanced_segments:list', APP_KEY),
          hidden: !features[Features.SegmentsRead],
        },
        {
          divider: true,
          label: 'Contact Management',
          url: getUrl('push:contact_management', APP_KEY),
          hidden: !features[Features.DeviceLookup],
        },
        {
          label: 'Preview and Test Groups',
          url: getUrl('test_devices:apps_test_lists', APP_KEY),
          hidden: !features[Features.PushComposer],
        },
        {
          divider: true,
          label: 'Attributes',
          url: getUrl('attributes:apps_attributes_index', APP_KEY),
          hidden: !features[Features.SegmentsRead],
        },
        {
          label: 'Lists',
          url: getUrl('static_lists:static_lists_list', APP_KEY),
          hidden: !features[Features.StaticLists],
        },
        {
          label: 'Tags',
          url: getUrl('tags:tag-index', APP_KEY),
          hidden: !features[Features.Tags],
        },
      ],
    },
    {
      label: 'Experiments',
      children: [
        {
          label: 'Experimentation Hub',
          isNew: true,
          url: getUrl(
            'experiments_overview:apps_experiments_overview_index',
            APP_KEY
          ),
          hidden:
            !features[Features.ExperimentsOverview] ||
            !features[Features.Insight],
          onSelect: () => {
            track('Experiments Overview', 'Click', 'Navigate', {
              experimentsHubSection: 'Home',
            })
          },
        },
        {
          label: 'Holdout Experiments',
          url: getUrl('experiments:apps_holdout_groups', APP_KEY),
          hidden: !features[Features.HoldoutExperiments],
        },
        {
          label: 'Feature Flags',
          url: getUrl('feature_flags:apps_feature_flags_index', APP_KEY),
          hidden: !features[Features.FeatureFlagsHomeAccess],
          onSelect: () => {
            track(
              'Feature Flags',
              'Click',
              'Feature Flag section accessed via Nav',
              {url: window.location.href}
            )
          },
        },
      ],
      hidden:
        !features[Features.FeatureFlagsHomeAccess] &&
        !features[Features.HoldoutExperiments] &&
        !features[Features.ExperimentsOverview],
    },
    {
      label: 'Reports',
      children: [
        {
          label: 'Performance Analytics',
          url: features[Features.Insight]
            ? getUrl('looker:apps_looker_dashboard', APP_KEY)
            : getUrl('looker:apps_insight_onboarding', APP_KEY),
        },
        {
          label: 'App Health',
          url: getUrl('js-urls:reports:reporting_hub', APP_KEY),
          hidden: !features[Features.Insight],
          onSelect: () => {
            track('Reporting Hub', 'Click', 'Navigate', {
              reportingHubSection: 'Home',
            })
          },
        },
        {
          label: 'App Metrics',
          url: getUrl('reports:apps_reports_app_metrics', APP_KEY),
        },
        {
          label: 'Unique App Opens',
          url: getUrl('reports:apps_reports_unique_app_opens', APP_KEY),
          hidden: !features[Features.ReportsOptin],
        },
        {
          label: 'Push Response',
          url: getUrl('reports:apps_reports_response', APP_KEY),
          hidden: !features[Features.ReportsResponse],
        },
        {
          label: 'Event Tracking',
          url: getUrl('reports:apps_reports_aggregate_events', APP_KEY),
          hidden: !features[Features.CustomEventsRead],
        },
        {
          label: 'Goals',
          url: getUrl('goals:apps_goals_index', APP_KEY),
        },
        {
          label: 'Devices',
          url: getUrl('reports:apps_reports_devices', APP_KEY),
          hidden: !features[Features.ReportsAudience],
        },
        {
          label: 'Email',
          url: getUrl('reports:apps_reports_emails', APP_KEY),
          hidden: !features[Features.ReportsEmail],
        },
        {
          label: 'SMS',
          url: getUrl('reports:apps_reports_sms', APP_KEY),
          hidden: !features[Features.ReportsSms],
        },
        {
          label: 'Web',
          url: getUrl('reports:apps_reports_web_push', APP_KEY),
          hidden: !features[Features.ReportsWeb],
        },
        {
          label: 'Surveys',
          url: getUrl('reports:apps_reports_surveys', APP_KEY),
          hidden: !features[Features.Survey] && !features[Features.Scene],
        },
        {
          label: 'Delivery Time',
          url: `${getUrl(
            'js-urls:reports:reporting_hub',
            APP_KEY
          )}library/delivery`,
          hidden: !waffle.flag_is_active(WaffleFlag.OlympicsDeliveryTime),
        },
      ],
      hidden: !features[Features.Reports],
    },
    {
      label: 'Settings',
      url: getUrl('apps:app_settings_dashboard', APP_KEY),
      hidden: !features[Features.SettingsPage],
    },
  ]
}
